import type { MessageView } from "@/api";

export type ReviewTabId = "draft" | "facts" | "positions" | "client";

/**
 * The four operator contexts of a request (#805), in the order they are worked through.
 *
 * Request history used to be a fifth peer tab, which put a customer's earlier enquiries as far
 * from that customer as the draft is. It is customer context, so it now sits inside `client`,
 * under the customer's details and addresses — one place to answer "who is this and what have
 * they asked us before".
 */
export const REVIEW_TABS: ReviewTabId[] = [
  "draft",
  "facts",
  "positions",
  "client",
];

export interface ReviewMessageEntry {
  key: string;
  message: MessageView;
  sourceIndex: number;
}

function messageTime(message: MessageView): number | null {
  if (!message.receivedAt) return null;
  const value = Date.parse(message.receivedAt);
  return Number.isFinite(value) ? value : null;
}

export function reviewMessageKey(message: MessageView, sourceIndex: number): string {
  return message.id || `message-${sourceIndex}`;
}

export function reviewMessageEntries(messages: MessageView[]): ReviewMessageEntry[] {
  return messages
    .map((message, sourceIndex) => ({
      key: reviewMessageKey(message, sourceIndex),
      message,
      sourceIndex,
    }))
    .filter(({ message }) => message.direction === "inbound" || message.direction === "outbound")
    .sort((left, right) => {
      const leftTime = messageTime(left.message);
      const rightTime = messageTime(right.message);
      if (leftTime !== null && rightTime !== null && leftTime !== rightTime) return rightTime - leftTime;
      if (leftTime !== null && rightTime === null) return -1;
      if (leftTime === null && rightTime !== null) return 1;
      return right.sourceIndex - left.sourceIndex;
    });
}

export function latestReviewMessageKey(messages: MessageView[]): string | null {
  return reviewMessageEntries(messages)[0]?.key ?? null;
}

export function reconcileReviewMessageSelection(
  messages: MessageView[],
  current: string | null,
): string | null {
  const entries = reviewMessageEntries(messages);
  if (current && entries.some((entry) => entry.key === current)) return current;
  return entries[0]?.key ?? null;
}

export interface ReviewActionModelInput {
  hasDraft: boolean;
  reviewState: string;
  guidanceState: string;
  nextAction: string;
  proposalControlsChanged: boolean;
  editing: boolean;
  decisionActionsBlocked: boolean;
  writerActionsBlocked: boolean;
  stale: boolean;
  humanReview: boolean;
  terminal: boolean;
  reconciliationPending: boolean;
}

export interface ReviewActionModel {
  canEdit: boolean;
  canReject: boolean;
  canApprove: boolean;
  canSend: boolean;
}

export function reviewActionModel(input: ReviewActionModelInput): ReviewActionModel {
  const draftMutationBlocked = input.terminal
    || input.reconciliationPending
    || input.writerActionsBlocked
    || input.decisionActionsBlocked
    || input.stale;
  return {
    canEdit: input.hasDraft && input.reviewState !== "rejected" && !draftMutationBlocked,
    canReject: input.hasDraft && input.reviewState !== "rejected" && !draftMutationBlocked,
    canApprove:
      input.hasDraft
      && input.reviewState !== "approved"
      && input.nextAction === "REVIEW_DRAFT"
      && !input.humanReview
      && !input.editing
      && !input.decisionActionsBlocked
      && !input.stale
      && !draftMutationBlocked
      && !input.proposalControlsChanged,
    canSend:
      input.hasDraft
      && input.guidanceState === "ready"
      && input.nextAction === "SEND_DRAFT"
      && !input.proposalControlsChanged
      && !input.editing
      && !input.decisionActionsBlocked
      && !draftMutationBlocked,
  };
}
